import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useLiveStructureSet } from '~/hooks/domain/useLiveStructureSet';
import { useStructureSetDetailView } from '~/hooks/domain/useStructureSetDetailView';
import { dicomViewerAtom } from '~/state/atoms/viewerAtoms';
import PropertiesPanel from './PropertiesPanel';

interface StructureSetPropertiesProps {
    structureSet: AvailableStructureSetView;
    onDelete?: () => void;
}

export default function StructureSetProperties({
    structureSet,
    onDelete,
}: StructureSetPropertiesProps) {
    const intl = useIntl();
    const dash = '—';
    const detail = useStructureSetDetailView(structureSet.structureSetId);
    // Live SS reference is needed only for the "View DICOM" dataset
    // dialog, which renders the raw DICOM tag tree from the parsed
    // dataset. No service abstraction fits since the dataset wrapper
    // is a third-party (dcmjs) object surfaced as-is to the viewer.
    const liveSs = useLiveStructureSet(structureSet.structureSetId);
    const setDicomViewer = useSetRecoilState(dicomViewerAtom);

    const datasetWrapper = liveSs?.dicomStructureSet.datasetWrapper;
    const onViewDicom = useCallback(() => {
        if (!datasetWrapper) {
            return;
        }
        setDicomViewer({
            dataset: datasetWrapper,
            title: structureSet.displayName ?? 'Structure Set',
        });
    }, [datasetWrapper, structureSet.displayName, setDicomViewer]);

    return (
        <PropertiesPanel
            onViewDicom={datasetWrapper ? onViewDicom : undefined}
            onDelete={onDelete}
        >
            <LabelText>
                <FormattedMessage id="label" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {structureSet.displayName || dash}
                </SensitiveInfoTypography>
            </ValueText>

            {detail && (
                <>
                    <LabelText>
                        <FormattedMessage id="description" />
                    </LabelText>
                    <ValueText component="span">
                        {detail.description || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="approvalStatus" />
                    </LabelText>
                    <ValueText component="span">
                        {detail.approvalStatus}
                    </ValueText>

                    <LabelText>ROIs</LabelText>
                    <ValueText component="span">
                        {detail.numberOfStructures}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="manufacturer" />
                    </LabelText>
                    <ValueText component="span">
                        {detail.manufacturer || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="manufacturerModelName" />
                    </LabelText>
                    <ValueText component="span">
                        {detail.manufacturerModelName || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="softwareVersions" />
                    </LabelText>
                    <ValueText component="span">
                        {detail.softwareVersions || dash}
                    </ValueText>

                    <LabelText>
                        {intl.formatMessage({ id: 'frameOfReferenceUid' })}
                    </LabelText>
                    <IdField
                        id={detail.frameOfReferenceUid}
                        typographyProps={{ fontSize: 11 }}
                    />
                </>
            )}

            {structureSet.imageLabel && (
                <>
                    <LabelText>
                        <FormattedMessage id="registration.sourceImage" />
                    </LabelText>
                    <ValueText component="span">
                        <SensitiveInfoTypography>
                            {structureSet.imageLabel}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            <LabelText>
                {intl.formatMessage({ id: 'sopInstanceUid' })}
            </LabelText>
            <IdField
                id={structureSet.sopInstanceUid}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
